/*
 * RETIRED FROM THE SHIFT SHEET — UC-A (2 Oct 2026). Phase 12 retirement candidate; kept, not deleted.
 * Reason: C1–C3 (fixed support_ratio 'Required staff' / '2:1 (High-intensity)' text, timesheet-billing and 2:1 line-item strings conflict with Final PRD §21, §33–34, §39, §45).
 * Former caller: components/calendar/CalendarShiftDrawer.jsx (pre-UC-A). Remaining callers (out of UC-A scope, unchanged): pages/roster/SILRosterPage.jsx (SilRatioInlineWarning, RatioRulesScopeNote).
 */
import React from 'react';
import { AlertTriangle, CheckCircle2, XCircle, Info, Users, UserCheck } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// Phase A — SIL Ratio Safety Guardrails
// UI-only. Does NOT touch billing / invoice / claim / funding pipelines.
//
// Displays a per-shift ratio & billing-readiness panel so admins see at a
// glance whether a shift is safe to invoice. See:
//   docs/sil_ratio_phase_a_guardrails.md
//   docs/current_sil_ratio_workflow_audit.md
// ──────────────────────────────────────────────────────────────────────────

const RATIO_LABEL = {
  '1:1': 'Standard 1:1 support',
  '2:1': 'High-intensity (2 staff per participant)',
  '1:2': 'Shared SIL (1 staff for 2 participants)',
};

function computeBillingReadiness(shift) {
  const ratio = shift?.support_ratio || null;
  const assigned = (shift?.assigned_staff_ids?.length) || (shift?.assigned_staff?.length) || 0;
  const requiredStaff = shift?.required_staff || (ratio === '2:1' ? 2 : 1);
  const linked = shift?.linked_participants?.length || (shift?.participant_id ? 1 : 0);
  const requiredParticipants = ratio === '1:2' ? 2 : 1;
  // Phase B: when fan-out has run, this shift IS billing-ready via per-participant allocations.
  const fannedOut = !!shift?.fanout_version || !!shift?.shared_support;

  if (ratio === '1:2') {
    if (fannedOut) {
      return {
        level: 'ok',
        status: `Fanned out · v${shift.fanout_version || 1}`,
        headline: 'Shared Support — 1 worker, 2 participants. Fan-out complete.',
        detail: 'Per-participant billing allocations are active. Each participant will be invoiced individually for the full shift duration. Payroll remains a single record.',
        ratio, assigned, requiredStaff, linked, requiredParticipants,
      };
    }
    return {
      level: 'blocked',
      status: 'Not billing-ready',
      headline: 'Shared Support (1:2) is not yet billing-ready.',
      detail: 'Run the Phase B fan-out below to generate per-participant billing allocations. Without fan-out, only the primary participant would be billed — over-billing of participant 1 and under-billing of participant 2.',
      ratio, assigned, requiredStaff, linked, requiredParticipants,
    };
  }

  if (ratio === '2:1') {
    const covered = assigned >= requiredStaff;
    return {
      level: covered ? 'ok' : 'warn',
      status: covered ? 'Staff covered' : 'Understaffed',
      headline: covered
        ? '2:1 high-intensity shift — staff coverage complete.'
        : `Understaffed: ${assigned}/${requiredStaff} assigned. Each staff must submit their own timesheet.`,
      detail: 'Billing depends on each assigned staff personally submitting a timesheet. Ensure the service catalog item selected uses the 2:1 NDIS line-item code.',
      ratio, assigned, requiredStaff, linked, requiredParticipants,
    };
  }

  // Default / 1:1
  return {
    level: 'ok',
    status: 'Billing-ready · Standard workflow',
    headline: '1:1 standard workflow. Billing flows through normally.',
    detail: '',
    ratio: ratio || '1:1', assigned, requiredStaff, linked, requiredParticipants,
  };
}

const LEVEL_STYLE = {
  ok:      { panel: 'bg-emerald-50 border-emerald-200',       text: 'text-emerald-900', pill: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
  warn:    { panel: 'bg-amber-50 border-amber-300',           text: 'text-amber-900',   pill: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: AlertTriangle },
  blocked: { panel: 'bg-red-50 border-red-400 border-2',      text: 'text-red-900',     pill: 'bg-red-100 text-red-800 border-red-400',             Icon: XCircle },
};

function Row({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">
        {value}
        {hint ? <span className="text-muted-foreground font-normal ml-1">({hint})</span> : null}
      </span>
    </div>
  );
}

export function SilRatioSafetyPanel({ shift, compact = false }) {
  if (!shift) return null;
  // Only render when ratio or linked_participants signals this is a SIL-aware shift.
  const hasSignal = !!shift.support_ratio || (shift.linked_participants && shift.linked_participants.length > 1);
  if (!hasSignal) return null;

  const info = computeBillingReadiness(shift);
  const style = LEVEL_STYLE[info.level];
  const Icon = style.Icon;
  const ratioLabel = RATIO_LABEL[info.ratio] || info.ratio;

  return (
    <div
      className={`rounded-md border p-3 space-y-2 ${style.panel}`}
      role={info.level === 'blocked' ? 'alert' : 'status'}
      data-testid={`sil-ratio-safety-${info.level}`}
    >
      <div className={`flex items-start gap-2 ${style.text}`}>
        <Icon className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold">{info.headline}</span>
            <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${style.pill}`} data-testid="sil-ratio-safety-status">
              {info.status}
            </span>
          </div>
          {info.detail && !compact && (
            <p className="text-[11px] mt-1 opacity-90 leading-snug">{info.detail}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 pt-1 border-t border-current/10">
        <Row label="Support ratio" value={info.ratio || '—'} hint={ratioLabel} />
        <Row label="Required staff" value={info.requiredStaff} />
        <Row label="Assigned staff" value={info.assigned} />
        <Row label="Required participants" value={info.requiredParticipants} />
        <Row label="Linked participants" value={info.linked} />
        <Row label="Billing status" value={info.status} />
      </div>
    </div>
  );
}


// Smaller inline banner for the SIL Roster legacy create form.
// Also used on the Scheduling Board toolbar when a selected shift is 1:2.
export function SilRatioInlineWarning({ ratio, fannedOut }) {
  if (ratio !== '1:2') return null;
  if (fannedOut) {
    return (
      <div
        role="status"
        className="rounded-md border-2 border-emerald-300 bg-emerald-50 text-emerald-900 p-2.5 flex items-start gap-2"
        data-testid="sil-ratio-1-2-fanned-out"
      >
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="text-[11px] leading-snug">
          <div className="font-semibold">Shared Support — fanned out.</div>
          <div className="opacity-90">Per-participant billing allocations active. Each participant will be invoiced for the full duration.</div>
        </div>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-md border-2 border-red-400 bg-red-50 text-red-900 p-2.5 flex items-start gap-2"
      data-testid="sil-ratio-1-2-warning"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="text-[11px] leading-snug">
        <div className="font-semibold">Shared Support (1:2) — not yet billing-ready.</div>
        <div className="opacity-90">
          Run the Phase B fan-out from the shift drawer to generate per-participant billing allocations.
        </div>
      </div>
    </div>
  );
}


// Small informational note clarifying that Ratio Rules ≠ SIL Ratios.
// Rendered on the SIL Roster page header and in the Scheduling Board toolbar.
export function RatioRulesScopeNote({ className = '' }) {
  return (
    <div
      className={`inline-flex items-start gap-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 ${className}`}
      data-testid="ratio-rules-scope-note"
    >
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
      <span>
        <strong>Note:</strong> Ratio Rules currently apply to <em>Group Sessions only</em>, not SIL rostering. SIL shift ratios are configured directly on each shift.
      </span>
    </div>
  );
}
